/**
 * Persistencia del modo demostracion.
 *
 * Guarda el conjunto de datos generado en `localStorage` para que lo que
 * hagas (inscribirte, enviar una marca, aprobarla desde el panel) siga ahi al
 * recargar. Si el almacenamiento no esta disponible —ventana privada, cuota
 * llena— la demo sigue funcionando solo en memoria.
 */

import { buildDemoData, type DemoData } from './seed';

const STORAGE_KEY = 'cp.demo.data';
const SESSION_KEY = 'cp.demo.session';
/** Subir esta version regenera los datos: util al cambiar el seed. */
const SCHEMA_VERSION = 11;

interface StoredEnvelope {
  version: number;
  /** Semana ISO en la que se genero, para refrescar la demo cada lunes. */
  generatedWeek: string;
  data: DemoData;
}

function weekTag(date: Date): string {
  const monday = new Date(date);
  monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7));
  return monday.toISOString().slice(0, 10);
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Cuota llena o almacenamiento bloqueado: la demo sigue en memoria.
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nada que hacer */
  }
}

let memory: DemoData | null = null;

/** Datos de la demo, regenerados si no hay nada guardado o cambio la semana. */
export function loadDemoData(): DemoData {
  if (memory) return memory;

  const raw = safeGet(STORAGE_KEY);
  const currentWeek = weekTag(new Date());

  if (raw) {
    try {
      const parsed = JSON.parse(raw) as StoredEnvelope;
      if (parsed.version === SCHEMA_VERSION && parsed.generatedWeek === currentWeek) {
        memory = parsed.data;
        return memory;
      }
    } catch {
      // JSON corrupto: se regenera desde cero.
    }
  }

  memory = buildDemoData(new Date());
  persist();
  return memory;
}

/** Vuelca el estado actual al almacenamiento. */
export function persist(): void {
  if (!memory) return;
  const envelope: StoredEnvelope = {
    version: SCHEMA_VERSION,
    generatedWeek: weekTag(new Date()),
    data: memory,
  };
  safeSet(STORAGE_KEY, JSON.stringify(envelope));
}

/** Aplica una mutacion sobre los datos y la guarda. */
export function mutate<T>(fn: (data: DemoData) => T): T {
  const data = loadDemoData();
  const result = fn(data);
  persist();
  return result;
}

/** Descarta todo y vuelve a los datos de ejemplo originales. */
export function resetDemoData(): void {
  memory = null;
  safeRemove(STORAGE_KEY);
  safeRemove(SESSION_KEY);
}

// --- Sesion ----------------------------------------------------------------

export function loadDemoSession(): string | null {
  return safeGet(SESSION_KEY);
}

export function saveDemoSession(userId: string | null): void {
  if (userId) safeSet(SESSION_KEY, userId);
  else safeRemove(SESSION_KEY);
}
